import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import {
  CaseImage,
  CreateCancellationRequest,
  LpQueue,
  Review,
  ReviewAssignmentDto,
  ReviewAssignmentHasAnyDto,
  ReviewSubjectType,
  SubmitErrorRequest,
  SubmitReviewRequest,
  SubmitValidationReviewRequest,
  UploadVideoClipsRequest,
  VideoSnapshot,
} from '../types';
import { AddCancellationVideoSnapshotsRequest, NtfReasonCategory, ProblemReason, VideoClip } from '../types/review';

@Injectable({
  providedIn: 'root',
})
export class LossPreventionService {
  apiHost = `${environment.apiBaseUrl}`;

  private httpClient = inject(HttpClient);

  createMultipleAssignments(
    location_id: number,
    review_message: string,
    review_subject_type: ReviewSubjectType,
    add_to_queue: boolean,
    num_to_create: number,
  ) {
    const requestUrl = `${this.apiHost}/LossPrevention/MultipleAssignments`;
    return this.httpClient.post<any>(requestUrl, {
      location_id,
      notes: review_message,
      review_subject_type,
      add_to_queue,
      num_to_create,
    });
  }

  renewReviewLease(reviewId: string) {
    const requestUrl = `${this.apiHost}/LossPrevention/ReviewReviewLease/${reviewId}`;
    return this.httpClient.post<string>(requestUrl, null);
  }

  uploadFiles(files: File[], filePath?: string, filePaths?: string[]) {
    const formData = new FormData();
    for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
      formData.append('files', files[fileIndex]);
    }
    if (Array.isArray(filePaths) && filePaths.length === files.length) {
      for (const p of filePaths) formData.append('file_paths', p);
    } else if (filePath) {
      formData.set('file_path', filePath);
    }
    const requestUrl = `${this.apiHost}/LossPrevention/UploadFiles`;
    return this.httpClient.post<string[]>(requestUrl, formData);
  }

  uploadVideoClips(files: File[], request: UploadVideoClipsRequest) {
    const formData = new FormData();
    for (const f of files) formData.append('files', f);
    formData.set('file_path', request.filePath);
    if (request.cancellationUniqueId) formData.set('cancellation_unique_id', String(request.cancellationUniqueId));
    if (request.reviewId) formData.set('review_id', String(request.reviewId));
    if (request.cancellationOffsetSeconds) formData.set('cancellation_offset_seconds', String(request.cancellationOffsetSeconds));
    const url = `${this.apiHost}/Video`;
    return this.httpClient.post<VideoClip[]>(url, formData);
  }

  getVideoClip(videoClipId: string) {
    const url = `${this.apiHost}/Video/Clips/${videoClipId}`;
    return this.httpClient.get<VideoClip>(url);
  }

  extendVideoClip(clipId: string, seconds: number) {
    const url = `${this.apiHost}/Video/Clips/${clipId}/Extend/${seconds}`;
    return this.httpClient.post(url, null);
  }

  addVideoSnapshots(referenceId: string, request: AddCancellationVideoSnapshotsRequest) {
    const url = `${this.apiHost}/Transactions/Cancellations/${referenceId}/VideoSnapshots`;
    return this.httpClient.post<VideoSnapshot[]>(url, request);
  }

  /**
   * this covers cancellation & sideloaded reviews
   */
  submitReview(reviewId: string, request: SubmitReviewRequest) {
    const requestUrl = `${this.apiHost}/LossPrevention/SubmitReview/${reviewId}`;
    return this.httpClient.post<void>(requestUrl, request);
  }

  /**
   * this covers validation reviews. it's in a separate method because validations have more side effects in the backend
   */
  submitValidation(reviewId: string, request: SubmitValidationReviewRequest) {
    const requestUrl = `${this.apiHost}/LossPrevention/SubmitValidation/${reviewId}`;
    return this.httpClient.post<void>(requestUrl, request);
  }

  submitSelfServiceReview(branchId: number, locationId: number, request: SubmitReviewRequest) {
    const url = `${this.apiHost}/LossPrevention/SelfService/Branch/${branchId}/Location/${locationId}/Review`;
    return this.httpClient.post<void>(url, request);
  }

  /**
   * force a validation right now
   * @param reviewId - review ID
   */
  submitReviewForceValidate(reviewId: string) {
    const requestUrl = `${this.apiHost}/LossPrevention/SubmitReview/${reviewId}/ForceValidate`;
    return this.httpClient.put<void>(requestUrl, {});
  }

  recoverReview(reviewId: string) {
    const requestUrl = `${this.apiHost}/Assignments/${reviewId}/Recover`;
    return this.httpClient.patch<Review>(requestUrl, {});
  }

  submitError(reviewId: string, request: SubmitErrorRequest) {
    const requestUrl = `${this.apiHost}/LossPrevention/SubmitError/${reviewId}`;
    return this.httpClient.post<void>(requestUrl, request);
  }

  refreshVideoPassword(review_assignment_id: any) {
    const requestUrl = `${this.apiHost}/LossPrevention/Review/${review_assignment_id}/VideoPassword/Refresh`;
    return this.httpClient.put<any>(requestUrl, {});
  }

  clearVideoPassword(review_assignment_id: any) {
    const requestUrl = `${this.apiHost}/LossPrevention/Review/${review_assignment_id}/VideoPassword/Clear`;
    return this.httpClient.put<any>(requestUrl, {});
  }

  checkExistingAssignment(locationId?: number) {
    const requestUrl = `${this.apiHost}/LossPrevention/CheckExistingAssignment`;
    let params = new HttpParams();
    if (locationId) params = params.set('location_id', locationId.toString());
    return this.httpClient.get<ReviewAssignmentDto>(requestUrl, { params });
  }

  hasAnyAvailableAssignments(locationId?: number) {
    const requestUrl = `${this.apiHost}/LossPrevention/HasAnyAvailableAssignments`;
    let params = new HttpParams();
    if (locationId) params = params.set('location_id', locationId.toString());
    return this.httpClient.get<ReviewAssignmentHasAnyDto | null>(requestUrl, { params });
  }

  uploadReviewPrioritiesCsv(fileHandle: File) {
    const formData = new FormData();
    formData.append(`files`, fileHandle);
    const requestUrl = `${this.apiHost}/LossPrevention/UploadReviewPrioritiesCsv`;
    return this.httpClient.post<any>(requestUrl, formData);
  }

  uploadTransactionsFile(fileHandle: File) {
    const formData = new FormData();
    formData.append(`files`, fileHandle);
    const requestUrl = `${this.apiHost}/LossPrevention/UploadTransactions`;
    return this.httpClient.post<any>(requestUrl, formData);
  }

  getCountOfPrioritizedLocations() {
    const requestUrl = `${this.apiHost}/LossPrevention/GetCountOfPrioritizedLocations`;
    return this.httpClient.get<number>(requestUrl);
  }

  getLocationInstructions(locationId: number) {
    const requestUrl = `${this.apiHost}/LossPrevention/Location/${locationId}/Instructions`;
    return this.httpClient.get<{ success: boolean; instructions: string }>(requestUrl);
  }

  updateLocationInstructions(locationId: number, instructions: string) {
    const requestUrl = `${this.apiHost}/LossPrevention/Location/${locationId}/Instructions`;
    return this.httpClient.patch(requestUrl, { instructions });
  }

  getProblemReasons() {
    return this.httpClient.get<ProblemReason[]>(`${this.apiHost}/Assignments/ProblemReasons`);
  }

  getNtfReasons() {
    return this.httpClient.get<NtfReasonCategory[]>(`${this.apiHost}/Assignments/NtfReasons`);
  }

  deleteReviewOutcome(reviewOutcomeIdToDelete: any) {
    const requestUrl = `${this.apiHost}/LossPrevention/ReviewOutcome/${reviewOutcomeIdToDelete}`;
    return this.httpClient.delete<any>(requestUrl);
  }

  reportVideoClip(reviewId: string, reason: string, videoClipId: string) {
    const requestUrl = `${this.apiHost}/LossPrevention/ReportCancellationClip`;
    return this.httpClient.post<any>(requestUrl, {
      review_id: reviewId,
      video_clip_id: videoClipId,
      reason,
    });
  }

  reportWalkout(reviewId: string, reason: string, videoClipId: string) {
    const requestUrl = `${this.apiHost}/LossPrevention/ReportWalkoutClip`;
    return this.httpClient.post<any>(requestUrl, {
      review_id: reviewId,
      walkout_video_clip_id: videoClipId,
      reason,
    });
  }

  // reminder that this is only for self-service users (other reviews will have "case_images" as part of response)
  getCasesByLocationSelfService(locationId: number) {
    return this.httpClient.get<CaseImage[]>(`${this.apiHost}/LossPrevention/Location/${locationId}/Cases`);
  }

  patchBranchesKeystore(branchId: number, key: string, value: string | boolean) {
    const data = { changes: { [branchId]: { [key]: value } } };
    return this.httpClient.patch<void>(`${this.apiHost}/LossPrevention/PatchBranchesKeystore`, data);
  }

  getQueueByReviewId(reviewId: string) {
    const requestUrl = `${this.apiHost}/LossPrevention/Queues/Review/${reviewId}`;
    return this.httpClient.get<LpQueue>(requestUrl);
  }

  createCancellation(request: CreateCancellationRequest) {
    const url = `${this.apiHost}/Transactions/Cancellations`;
    return this.httpClient.post<{ id: number }>(url, request);
  }
}
